import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle, XCircle, Loader } from "lucide-react";

export default function BlogDebugger() {
  const [debugInfo, setDebugInfo] = useState({
    status: "idle",
    rawResponse: null,
    processedData: null,
    error: null,
    steps: []
  });

  const addStep = (step) => {
    setDebugInfo(prev => ({
      ...prev,
      steps: [...prev.steps, { ...step, timestamp: new Date().toISOString() }]
    }));
  };

  useEffect(() => {
    const fetchAndDebug = async () => {
      try {
        addStep({ type: "info", message: "🚀 Starting API call to Strapi..." });
        setDebugInfo(prev => ({ ...prev, status: "loading" }));

        // Make the API call
        const res = await fetch("http://localhost:1337/api/blogs?populate=*");
        
        addStep({ 
          type: "success", 
          message: `✅ API Response Status: ${res.status} ${res.statusText}` 
        });

        if (!res.ok) {
          throw new Error(`HTTP Error: ${res.status}`);
        }

        const data = await res.json();
        
        addStep({ 
          type: "success", 
          message: `✅ Raw data received: ${data.data?.length || 0} blog(s) found` 
        });

        setDebugInfo(prev => ({ ...prev, rawResponse: data }));

        // Process the data
        addStep({ type: "info", message: "🔄 Processing blog data..." });

        const blogData = data.data.map((item, index) => {
          addStep({ 
            type: "info", 
            message: `Processing blog #${index + 1}: "${item.attributes?.title || 'No title'}"` 
          });

          const processed = {
            id: item.id,
            title: item.attributes?.title,
            slug: item.attributes?.slug,
            excerpt: item.attributes?.excerpt,
            content: item.attributes?.content,
            featuredImage: item.attributes?.featuredImage?.data?.attributes?.url
              ? `http://localhost:1337${item.attributes.featuredImage.data.attributes.url}`
              : "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
            date: item.attributes?.publishedDate || item.attributes?.createdAt,
            author: item.attributes?.author || "Landmark Realty",
            category: item.attributes?.category || "Guides",
            readTime: "5 min read",
            views: "1.2k",
          };

          addStep({ 
            type: "success", 
            message: `✅ Processed: "${processed.title}" - ${processed.category}` 
          });

          return processed;
        });

        setDebugInfo(prev => ({ 
          ...prev, 
          processedData: blogData,
          status: "success" 
        }));

        addStep({ 
          type: "success", 
          message: `🎉 Successfully processed ${blogData.length} blog post(s)!` 
        });

      } catch (err) {
        addStep({ 
          type: "error", 
          message: `❌ Error: ${err.message}` 
        });
        
        setDebugInfo(prev => ({ 
          ...prev, 
          error: err.message,
          status: "error" 
        }));

        console.error("Full error:", err);
      }
    };

    fetchAndDebug();
  }, []);

  const getStatusIcon = () => {
    switch (debugInfo.status) {
      case "loading":
        return <Loader className="w-6 h-6 text-blue-500 animate-spin" />;
      case "success":
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case "error":
        return <XCircle className="w-6 h-6 text-red-500" />;
      default:
        return <AlertCircle className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStepIcon = (type) => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "error":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "info":
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6">
            <div className="flex items-center gap-4">
              {getStatusIcon()}
              <div>
                <h1 className="text-3xl font-bold text-white">
                  Blog Fetch Debugger
                </h1>
                <p className="text-white/80 mt-1">
                  Status: <span className="font-semibold capitalize">{debugInfo.status}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Debug Steps */}
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">
              📋 Debug Steps
            </h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {debugInfo.steps.map((step, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-950 rounded-lg"
                >
                  {getStepIcon(step.type)}
                  <div className="flex-1">
                    <p className="text-sm text-slate-700 dark:text-slate-200 font-mono">
                      {step.message}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(step.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Raw Response */}
          {debugInfo.rawResponse && (
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                📦 Raw API Response
              </h2>
              <pre className="bg-slate-900 text-green-400 p-4 rounded-lg overflow-x-auto text-xs">
                {JSON.stringify(debugInfo.rawResponse, null, 2)}
              </pre>
            </div>
          )}

          {/* Processed Data */}
          {debugInfo.processedData && (
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                ✨ Processed Blog Data
              </h2>
              <div className="space-y-4">
                {debugInfo.processedData.map((post) => (
                  <div
                    key={post.id}
                    className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-xl border border-purple-200"
                  >
                    <div className="flex gap-4">
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        className="w-32 h-32 object-cover rounded-lg"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/150?text=Image+Error";
                        }}
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">
                              {post.title}
                            </h3>
                            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                              {post.excerpt}
                            </p>
                          </div>
                          <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-xs font-bold">
                            {post.category}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                          <span>📅 {new Date(post.date).toLocaleDateString()}</span>
                          <span>👤 {post.author}</span>
                          <span>⏱️ {post.readTime}</span>
                          <span>👁️ {post.views} views</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error Display */}
          {debugInfo.error && (
            <div className="p-6 bg-red-50">
              <h2 className="text-xl font-bold text-red-800 mb-4 flex items-center gap-2">
                <XCircle className="w-6 h-6" />
                Error Details
              </h2>
              <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded">
                <p className="text-red-800 font-mono text-sm">
                  {debugInfo.error}
                </p>
              </div>
              <div className="mt-4 p-4 bg-white dark:bg-slate-900 rounded-lg">
                <h3 className="font-bold text-slate-800 dark:text-slate-100 mb-2">
                  Common Issues:
                </h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-300">
                  <li>Is Strapi running on http://localhost:1337?</li>
                  <li>Check CORS settings in Strapi</li>
                  <li>Verify the API endpoint is correct</li>
                  <li>Check if the blog content type exists</li>
                  <li>Ensure you have at least one published blog post</li>
                </ul>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="p-6 bg-slate-50 dark:bg-slate-950">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">
              📊 Summary
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow">
                <p className="text-sm text-slate-600 dark:text-slate-300">Total Blogs</p>
                <p className="text-2xl font-bold text-purple-600">
                  {debugInfo.processedData?.length || 0}
                </p>
              </div>
              <div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow">
                <p className="text-sm text-slate-600 dark:text-slate-300">API Status</p>
                <p className="text-2xl font-bold text-blue-600 capitalize">
                  {debugInfo.status}
                </p>
              </div>
              <div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow">
                <p className="text-sm text-slate-600 dark:text-slate-300">Debug Steps</p>
                <p className="text-2xl font-bold text-green-600">
                  {debugInfo.steps.length}
                </p>
              </div>
              <div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow">
                <p className="text-sm text-slate-600 dark:text-slate-300">Errors</p>
                <p className="text-2xl font-bold text-red-600">
                  {debugInfo.error ? "1" : "0"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}